import { defineStore } from 'pinia';
import { getFriendList, getContactTags, getContactProfile } from '../api/user.js'

export default defineStore('friendStore', {
	state: () => {
		return {
			friends: [],        // 好友列表（分页场景为已加载部分）
			total: 0,           // 服务端好友总数（分页滚动终止判断）
			loadedAll: false,   // 是否已加载全部（兼容旧后端/全量模式恒为 true）
			friendLoading: false, // 分页加载中标记（防重复触发）
			allTags: [],        // 所有可用标签
			filter: {           // 当前筛选条件
				keyword: '',
				tags: [],
				stage: '',
				dateFrom: null,
				dateTo: null
			},
			activeProfile: null, // 当前查看的客户详情
			profileLoading: false,
			profileSeq: 0,      // 请求序号，用于丢弃过时响应
			// 好友列表请求序号/当前页：必须在 state 初始化，否则首次 ++undefined = NaN，
			// 导致 seq !== this._loadSeq 恒真（NaN !== NaN），所有响应被当作过时请求静默丢弃
			_loadSeq: 0,
			_friendPage: 0
		}
	},
	actions: {
		setFriends(friends) {
			this.friends = Array.isArray(friends) ? friends : [];
		},
		setAllTags(tags) {
			this.allTags = Array.isArray(tags) ? tags : [];
		},
		setActiveProfile(profile) {
			this.activeProfile = profile;
		},
		setFilter(f) {
			Object.assign(this.filter, f);
		},
		resetFilter() {
			this.filter = { keyword: '', tags: [], stage: '', dateFrom: null, dateTo: null };
		},
		updateFriend(friend) {
			this.friends.forEach((f, index) => {
				if (f.userID == friend.userID) {
					Object.assign(this.friends[index], friend);
				}
			})
		},
		removeFriend(userID) {
			this.friends.filter(f => f.userID == userID).forEach(f => f.deleted = true);
		},
		addFriend(friend) {
			if (this.friends.some(f => f.userID == friend.userID)) {
				this.updateFriend(friend)
			} else {
				this.friends.unshift(friend);
			}
		},
		clear() {
			this.friends = [];
			this.total = 0;
			this.loadedAll = false;
			this.friendLoading = false;
			this._friendPage = 0;
			this._loadSeq = 0;
			this.allTags = [];
			this.activeProfile = null;
			this.resetFilter();
		},
		// 加载好友列表
		// reset=true: 从第 1 页重新加载（筛选变化/初始化）；reset=false: 追加下一页（滚动加载）
		// pageSize=0: 全量返回（AddGroupMember 等需要完整列表的场景，兼容旧后端数组响应）
		// 并发控制采用请求序号模式：后发起的请求取代先前的（旧响应到达时直接丢弃），
		// 避免"加载中吞掉 reset/全量请求"导致筛选失效或面板数据不全
		loadFriend({ reset = true, pageSize = 0 } = {}) {
			const seq = ++this._loadSeq;
			this.friendLoading = true;
			const page = reset ? 1 : (this._friendPage || 0) + 1;
			const params = {};
			if (pageSize > 0) {
				params.page = page;
				params.pageSize = pageSize;
				// 筛选条件仅作用于分页视图（服务端筛选）；全量模式（pageSize=0）恒返回
				// 默认完整列表，避免断线重连/补拉等场景带残留 filter 拉回筛选结果污染完整视图
				if (this.filter.keyword) params.keyword = this.filter.keyword;
				if (this.filter.tags && this.filter.tags.length > 0) params.tags = this.filter.tags.join(',');
				if (this.filter.stage) params.stage = this.filter.stage;
				if (this.filter.dateFrom) params.dateFrom = this.filter.dateFrom;
				if (this.filter.dateTo) params.dateTo = this.filter.dateTo;
			}

			return new Promise((resolve, reject) => {
				console.log('[friendStore] loadFriend 开始..., reset:', reset, 'page:', page, 'params:', params);
				getFriendList(params).then((res) => {
					if (seq !== this._loadSeq) {
						resolve(); // 过时响应：完成但不应用结果（新请求已接管），避免 Promise 悬挂阻塞调用方
						return;
					}
					// 兼容：新后端 {list,total,page,pageSize}；旧后端/全量模式为数组
					const list = Array.isArray(res) ? res : (res && Array.isArray(res.list) ? res.list : []);
					const total = Array.isArray(res) ? list.length : ((res && res.total) || 0);
					if (reset) {
						this.friends = list;
						this._friendPage = 1;
					} else {
						// 追加去重（防止游标重叠/数据漂移）
						const seen = new Set(this.friends.map(f => f.userID));
						const append = list.filter(f => !seen.has(f.userID));
						this.friends = this.friends.concat(append);
						this._friendPage = page;
					}
					this.total = total;
					// 全量模式/已拉完/末页为空（数据增长期 COUNT 大于实际）均视为加载完成
					this.loadedAll = pageSize <= 0 || list.length === 0 || this.friends.length >= total;
					console.log('[friendStore] loadFriend 成功, 好友数量:', this.friends.length, 'total:', total, 'loadedAll:', this.loadedAll);
					resolve();
				}).catch(e => {
					console.error('[friendStore] loadFriend 失败:', e);
					reject(e);
				}).finally(() => {
					this.friendLoading = false;
				})
			});
		},
		loadTags() {
			return getContactTags().then(res => {
				this.setAllTags(res && res.tags ? res.tags : []);
			}).catch(e => {
				console.error('[friendStore] loadTags 失败:', e);
			});
		},
		loadProfile(vid, userID) {
			const seq = ++this.profileSeq;
			this.profileLoading = true;
			return getContactProfile({ vid, userID }).then(res => {
				// 仅当请求未过期时才应用结果
				if (seq === this.profileSeq) {
					this.setActiveProfile(res);
				}
				return res;
			}).catch(e => {
				console.error('[friendStore] loadProfile 失败:', e);
				throw e;
			}).finally(() => {
				if (seq === this.profileSeq) {
					this.profileLoading = false;
				}
			});
		}
	},
	getters: {
		// 是否存在生效中的筛选条件：筛选会以服务端结果整体替换 friends（临时视图），
		// 离开/返回好友页与选人面板需据此识别"friends 已被筛选污染"，强制恢复默认完整列表
		hasActiveFilter: (state) => {
			return !!(state.filter.keyword || state.filter.stage || (state.filter.tags && state.filter.tags.length > 0));
		},
		isFriend: (state) => (userID) => {
			return state.friends.filter((f) => !f.deleted).some((f) => f.userID == userID);
		},
		findFriend: (state) => (userID) => {
			return state.friends.find((f) => f.userID == userID);
		},
		stageCounts: (state) => {
			const counts = { new: 0, active: 0, silent: 0, churned: 0 };
			state.friends.forEach(f => {
				if (!f.deleted && f.stage && counts[f.stage] !== undefined) {
					counts[f.stage]++;
				}
			});
			return counts;
		}
	}
});
